import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Utensils, ChefHat, Menu as MenuIcon, BookOpen, Package,
  Truck, Receipt, TrendingUp, FileText, BarChart3, BookMarked, Settings,
  Sparkles, Languages, Sun, Moon, X, LayoutGrid, CalendarCheck, Building2, ChevronDown,
  Users as UsersIcon, LogOut, User as UserIcon, ShieldCheck,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useI18n } from '@/lib/i18n';
import { useBranding } from '@/lib/branding';
import { useAuth, ROLE_LABELS, pathAllowed } from '@/lib/auth';
import { AiAssistantDrawer } from '@/components/AiAssistantDrawer';
import { AppFooter } from '@/components/AppFooter';
import { LocationSwitcher } from '@/components/LocationSwitcher';
import { cn } from '@/lib/utils';

const NAV_SECTIONS = (t) => [
  {
    label: t('section_operation'),
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: t('nav_dashboard'), tid: 'sidebar-nav-dashboard-link' },
      { to: '/pos', icon: Utensils, label: t('nav_pos'), tid: 'sidebar-nav-pos-link' },
      { to: '/floor-plan', icon: LayoutGrid, label: t('nav_floor_plan'), tid: 'sidebar-nav-floor-plan-link' },
      { to: '/reservations', icon: CalendarCheck, label: t('nav_reservations'), tid: 'sidebar-nav-reservations-link' },
      { to: '/kds', icon: ChefHat, label: t('nav_kds'), tid: 'sidebar-nav-kds-link' },
    ],
  },
  {
    label: t('section_product'),
    items: [
      { to: '/menu', icon: MenuIcon, label: t('nav_menu'), tid: 'sidebar-nav-menu-link' },
      { to: '/recipes', icon: BookOpen, label: t('nav_recipes'), tid: 'sidebar-nav-recipes-link' },
      { to: '/inventory', icon: Package, label: t('nav_inventory'), tid: 'sidebar-nav-inventory-link' },
      { to: '/suppliers', icon: Truck, label: t('nav_suppliers'), tid: 'sidebar-nav-suppliers-link' },
    ],
  },
  {
    label: t('section_finance'),
    items: [
      { to: '/expenses', icon: Receipt, label: t('nav_expenses'), tid: 'sidebar-nav-expenses-link' },
      { to: '/income', icon: TrendingUp, label: t('nav_income'), tid: 'sidebar-nav-income-link' },
      { to: '/invoices', icon: FileText, label: t('nav_invoices'), tid: 'sidebar-nav-invoices-link' },
      { to: '/reports', icon: BarChart3, label: t('nav_reports'), tid: 'sidebar-nav-reports-link' },
      { to: '/pgc', icon: BookMarked, label: t('nav_pgc'), tid: 'sidebar-nav-pgc-link' },
    ],
  },
  {
    label: t('section_system'),
    items: [
      { to: '/users', icon: UsersIcon, label: 'Usuarios / Usuários', tid: 'sidebar-nav-users-link' },
      { to: '/locations', icon: Building2, label: t('nav_locations'), tid: 'sidebar-nav-locations-link' },
      { to: '/settings', icon: Settings, label: t('nav_settings'), tid: 'sidebar-nav-settings-link' },
    ],
  },
];

function SidebarContent({ onNavigate }) {
  const { t } = useI18n();
  const { branding } = useBranding();
  const { user } = useAuth();
  const allSections = NAV_SECTIONS(t);
  // Filter items by current user's role
  const sections = user
    ? allSections
        .map((sec) => ({
          ...sec,
          items: sec.items.filter((it) => pathAllowed(user.role, it.to)),
        }))
        .filter((sec) => sec.items.length > 0)
    : allSections;
  return (
    <nav className="flex flex-col h-full overflow-y-auto py-3 px-2">
      <div className="px-3 py-2 mb-2">
        <div className="flex items-center gap-2">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt="logo" className="h-9 w-9 rounded-lg object-cover" />
          ) : (
            <div className="h-9 w-9 rounded-lg bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))] font-heading font-bold">
              {(branding?.nombre || 'R').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-heading font-bold text-base leading-tight truncate">{branding?.nombre || t('app_name')}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">PT/ES • PGC PYMES</div>
          </div>
        </div>
      </div>
      {sections.map((section) => (
        <div key={section.label} className="mb-3">
          <div className="px-3 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mt-3 mb-1">
            {section.label}
          </div>
          {section.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.tid}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  'hover:bg-[hsl(var(--muted))]',
                  isActive
                    ? 'bg-[hsl(var(--accent))] text-[hsl(var(--foreground))] border-l-2 border-[hsl(var(--secondary))]'
                    : 'text-[hsl(var(--muted-foreground))]'
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function Topbar({ onOpenSidebar, onOpenAi }) {
  const { lang, setLang, t } = useI18n();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));

  const toggleDark = () => {
    document.documentElement.classList.toggle('dark');
    setDark((d) => !d);
  };

  const titleMap = {
    '/dashboard': t('nav_dashboard'),
    '/pos': t('nav_pos'),
    '/floor-plan': t('nav_floor_plan'),
    '/reservations': t('nav_reservations'),
    '/kds': t('nav_kds'),
    '/menu': t('nav_menu'),
    '/recipes': t('nav_recipes'),
    '/inventory': t('nav_inventory'),
    '/suppliers': t('nav_suppliers'),
    '/expenses': t('nav_expenses'),
    '/income': t('nav_income'),
    '/invoices': t('nav_invoices'),
    '/reports': t('nav_reports'),
    '/pgc': t('nav_pgc'),
    '/locations': t('nav_locations'),
    '/settings': t('nav_settings'),
    '/users': lang === 'es' ? 'Usuarios' : 'Usuários',
  };
  const title = titleMap[location.pathname] || t('app_name');
  const roleLabel = user ? (ROLE_LABELS[lang] || ROLE_LABELS.es)[user.role] : '';

  return (
    <header className="sticky top-0 z-30 h-14 border-b bg-[hsl(var(--card))] flex items-center px-3 sm:px-4 gap-3">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden" data-testid="topbar-mobile-menu-button" aria-label="Menu">
            <MenuIcon className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-72">
          <SidebarContent onNavigate={onOpenSidebar} />
        </SheetContent>
      </Sheet>
      <div className="font-heading font-semibold text-base sm:text-lg truncate flex-1">{title}</div>
      <LocationSwitcher />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" data-testid="language-toggle-button" className="gap-1">
            <Languages className="h-4 w-4" />
            <span className="hidden sm:inline uppercase text-xs">{lang}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('settings_language')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setLang('es')} data-testid="language-option-es">
            🇪🇸 Español
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setLang('pt')} data-testid="language-option-pt">
            🇧🇷 Português
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button variant="ghost" size="icon" onClick={toggleDark} aria-label="Toggle dark" data-testid="theme-toggle-button">
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>
      <Button
        variant="default"
        size="sm"
        onClick={onOpenAi}
        className="gap-2 bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--brand-teal-2))] text-white"
        data-testid="ai-assistant-open-button"
      >
        <Sparkles className="h-4 w-4" />
        <span className="hidden sm:inline">{t('ai_assistant')}</span>
      </Button>
      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-button">
              <div className="h-7 w-7 rounded-full bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))] text-xs font-bold">
                {(user.nombre || user.email || '?').charAt(0).toUpperCase()}
              </div>
              <span className="hidden md:flex flex-col items-start leading-tight">
                <span className="text-xs font-medium truncate max-w-[120px]">{user.nombre}</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))] truncate max-w-[120px]">{roleLabel}</span>
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex items-center gap-2">
              <UserIcon className="h-3.5 w-3.5" />{user.email}
            </DropdownMenuLabel>
            <DropdownMenuLabel className="flex items-center gap-2 text-xs font-normal text-[hsl(var(--muted-foreground))]">
              <ShieldCheck className="h-3.5 w-3.5" />{roleLabel}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-red-600 focus:text-red-700">
              <LogOut className="h-4 w-4 mr-2" />
              {lang === 'es' ? 'Cerrar sesión' : 'Sair'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  );
}

export function AppShell({ children, fullBleed = false }) {
  const [aiOpen, setAiOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-[hsl(var(--background))] text-[hsl(var(--foreground))] app-noise">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r bg-[hsl(var(--card))] sticky top-0 h-screen">
        <SidebarContent />
      </aside>
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        <Topbar onOpenAi={() => setAiOpen(true)} />
        <main className={cn('flex-1', fullBleed ? '' : 'p-4 sm:p-6 max-w-[1400px] w-full')}>
          {children}
        </main>
        {!fullBleed && <AppFooter />}
      </div>
      <AiAssistantDrawer open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
}

export function FullScreenLayout({ children }) {
  // For KDS standalone fullscreen mode
  return <div className="min-h-screen bg-[hsl(var(--background))]">{children}</div>;
}
